import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { map, Observable } from 'rxjs';
import { PriceComponent } from '../../store/price.model';
import {
  BulkPriceDeletePayload,
  DeletePricePayload,
  DeletePriceResponse,
  ExportDailyPricePayload,
  PaginatedPriceList,
  PriceListPayload,
  SetPricePayload,
  SetPriceResponse
} from '../models/price.model';
import { AssociationTypesEnum } from '../enums/association-types.enum';
import { DatePipe, DecimalPipe } from '@angular/common';
import { UtilityService } from './utility.service';
import { ActiveTableFilters } from '../../shared/components/table/models/page.model';

@Injectable({
  providedIn: 'root'
})
export class PriceService {
  private http = inject(HttpClient);
  private decimalPipe = inject(DecimalPipe);
  private utilityService = inject(UtilityService);

  constructor() {}

  /**
   * Currently association type is hardcoded. No need to pass params
   * Gets the priceComponentId needed for price list
   */
  getPriceComponents(
    associationValue: string,
    associationType = AssociationTypesEnum.FACTORY_CODE
  ): Observable<PriceComponent[]> {
    return this.http.get<PriceComponent[]>(
      `${environment.AGTRACE_API}v1/price-components?associationType=${associationType}&associationValue=${associationValue}`
    );
  }

  getPaginatedPriceList(payload: PriceListPayload): Observable<PaginatedPriceList> {
    const activeTableFilters: ActiveTableFilters = {
      sort: payload?.sort,
      filters: payload?.filters
    };

    const filterQuery = this.buildActiveFiltersQuery(activeTableFilters);

    return this.http
      .get<PaginatedPriceList>(
        `${environment.AGTRACE_API}v1/price-components/prices?priceComponentId=${payload.priceComponentId}&page=${payload.page}&pageSize=${payload.pageSize}${filterQuery}`
      )
      .pipe(
        map((res) => {
          return {
            ...res,
            results: res.results?.map((item) => ({
              ...item,
              effectiveDate: item.effectiveDate,
              createdAt: item.createdAt,
              price: this.decimalPipe.transform(item.price, '1.2-2')!
            }))
          };
        })
      );
  }

  setPrice(payload: SetPricePayload): Observable<SetPriceResponse> {
    return this.http.post<SetPriceResponse>(`${environment.AGTRACE_API}v1/price-components/prices`, payload);
  }

  deletePrice(payload: DeletePricePayload): Observable<DeletePriceResponse> {
    return this.http.delete<DeletePriceResponse>(
      `${environment.AGTRACE_API}v1/price-components/prices?priceComponentId=${payload.priceComponentId}&priceId=${payload.priceId}`
    );
  }

  bulkDeletePrice(payload: BulkPriceDeletePayload): Observable<DeletePriceResponse[]> {
    const priceIdsQuery = this.utilityService.constructQueryParam('priceIds', payload.priceIds);
    return this.http.delete<DeletePriceResponse[]>(
      `${environment.AGTRACE_API}v1/price-components/prices/bulk${priceIdsQuery}&priceComponentId=${payload.priceComponentId}`
    );
  }

  exportDailyPriceReport(payload: ExportDailyPricePayload) {
    const filterQuery = this.buildActiveFiltersQuery(payload.activeTableFilters);

    return this.http.get<Blob>(
      `${environment.AGTRACE_API}v1/price-components/prices/export?priceComponentId=${payload.priceComponentId}${filterQuery}`,
      {
        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }

  private buildActiveFiltersQuery(activeFilters: ActiveTableFilters): string {
    const filters = activeFilters.filters ?? {};
    const sortQuery = activeFilters.sort ? `sort=${encodeURIComponent(activeFilters.sort)}` : '';
    const dynamicQueries: string[] = [];

    // Special handling for effectiveDate
    if (filters['effectiveDate']) {
      const { startDate, endDate } = this.utilityService.getDestructuredDateRange(filters['effectiveDate']);
      if (startDate) {
        dynamicQueries.push(`startDate=${encodeURIComponent(startDate)}`);
      }
      if (endDate) {
        dynamicQueries.push(`endDate=${encodeURIComponent(endDate)}`);
      }
    }

    const queryParts = [sortQuery, ...dynamicQueries].filter(Boolean);
    return queryParts.length > 0 ? `&${queryParts.join('&')}` : '';
  }
}
